import { useEffect, useMemo, useRef } from 'react'
import * as Analytics from 'analytics/analytics'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import type { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import {
  isFeatureFlagExperiment,
  getOptimizelyFeatureFlagVariation,
  getOptimizelyExperimentVariation,
  isAllowedToTriggerExperiment,
  setOptimizelyTriggeredCookie,
} from 'lib/optimizely/optimizelyUtils'
import { activateCampaignEvent } from 'analytics/snowplow/events'
import { setOptimizelyExperiment } from 'actions/OptimizelyActions'

const optimizelyExperimentSet = new Set(config.OPTIMIZELY_EXPERIMENTS)
const optimizelyFeatureFlagSet = new Set(config.OPTIMIZELY_FEATURE_FLAGS)
export const OptimizelyExperimentVariantTypeEnum = {
  variant_1: 'variant_1',
  variant_2: 'variant_2',
  variant_3: 'variant_3',
  variant_4: 'variant_4',
}

export type OptimizelyExperimentVariantType = keyof typeof OptimizelyExperimentVariantTypeEnum | undefined

/**
 * Optimizely Experiments Selectors.  Each experiment can be found in Redux in state.optimizely.optimizelyExperiments.
 * Each has an 'enabled' value that can be used to determine if the user is enrolled in a variant (if the user is
 * part of the control, or the test isn't live in Optimizely, the value will be false).  Each experiment also has
 * a 'variationKey' value that can be used to determine which variant the user is enrolled in.
 *
 * Return the optimizely experiment variant dynamically based on the given trigger boolean condition.
 * If canTriggerExperiment is false the experiment will not be activated.
 * Note: once the trigger becomes true once, it will be enabled for the rest of the session until page refresh.
 *
 * @param canTrigger a boolean indicating whether the experiment can be triggered
 * @param experimentId experiment id from the Optimizely UI (Rule Key)
 * @param overrideTrigger enable A/B testing for conditions that are typically excluded in the canTriggerExperiment useMemo
 * @returns Experiment variant if can be triggered
 */
function useOptimizelyExperiment(experimentId: OptimizelyExperiments, canTrigger?: boolean, overrideTrigger?: boolean): string | undefined
function useOptimizelyExperiment(experimentId: OptimizelyFeatureFlags, canTrigger?: boolean, overrideTrigger?: boolean): boolean | undefined
function useOptimizelyExperiment(experimentId: OptimizelyExperiments | OptimizelyFeatureFlags, canTrigger?: boolean, overrideTrigger?: boolean): string | boolean | undefined
function useOptimizelyExperiment(experimentId: OptimizelyExperiments | OptimizelyFeatureFlags, canTrigger: boolean = true, overrideTrigger = false) {
  const dispatch = useAppDispatch()
  const hasTriggeredEvent = useRef(false)
  const previousVariationKey = useRef<string | undefined>(undefined)
  const utmContent = useAppSelector(state => state.utm.content)

  const canTriggerExperiment = useMemo(() => {
    if (!optimizelyExperimentSet.has(experimentId) && !optimizelyFeatureFlagSet.has(experimentId)) {
      return false
    }
    if (overrideTrigger) {
      return canTrigger
    }
    return isAllowedToTriggerExperiment(canTrigger, utmContent, experimentId)
  }, [canTrigger, overrideTrigger, utmContent, experimentId])
  const experiment = useAppSelector(state =>
    isFeatureFlagExperiment(experimentId) ? undefined : state.optimizely.optimizelyExperiments[experimentId as OptimizelyExperiments])

  const experimentVariation = useAppSelector(state => {
    if (isFeatureFlagExperiment(experimentId)) {
      return getOptimizelyFeatureFlagVariation(state, experimentId)
    } else {
      return getOptimizelyExperimentVariation(state, experimentId)
    }
  })

  // Reset trigger tracking when variation changes due to rebucketing (login).
  // Rebucketing explicitly sets `triggered: false` for changed experiments — this is
  // the signal that distinguishes rebucketing from DevTools toggles (which preserve `triggered`
  // via the `force` parameter). Only reset when both conditions are met to avoid
  // unintended activation events from DevTools toggling.
  const currentVariationKey = experiment?.variationKey
  const experimentTriggered = experiment?.triggered
  useEffect(() => {
    if (currentVariationKey !== previousVariationKey.current) {
      if (hasTriggeredEvent.current && experimentTriggered === false) {
        hasTriggeredEvent.current = false
      }
      previousVariationKey.current = currentVariationKey
    }
  }, [currentVariationKey, experimentTriggered])

  useEffect(() => {
    if (canTriggerExperiment && !isFeatureFlagExperiment(experimentId) && !hasTriggeredEvent.current && !experiment?.triggered &&
      experiment?.campaignId &&
      experiment?.experimentId) {
      const variationId = experiment.variation?.[experiment.variationKey]
      if (variationId) {
        // Send Snowplow event to trigger user into Optimizely report
        Analytics.trackEvent(activateCampaignEvent(
          experiment.campaignId,
          experiment.experimentId,
          variationId,
        ))

        dispatch(setOptimizelyExperiment(experimentId, experiment))

        // Set cookie to prevent future triggering
        setOptimizelyTriggeredCookie(experimentId)
        hasTriggeredEvent.current = true
      }
    }
  }, [dispatch, canTriggerExperiment, experimentId, experiment])

  if (canTriggerExperiment) {
    // For devs to set a specific variation locally. Set REACT_APP_OPTIMIZELY_[experimentId]=true for variant or false for control in .env,
    if (config[`OPTIMIZELY_${experimentId}`] !== undefined) {
      return config[`OPTIMIZELY_${experimentId}`]
    }

    return experimentVariation
  }
}

export default useOptimizelyExperiment
